import { useMemo } from 'react';

import Analytics from '@hh.ru/analytics-js';

import { SettingName, SettingType } from 'src/models/search/resume/result';

import { useResumeSettingsViewFields } from 'src/hooks/resume/useResumeSettingsViewFields';
import { useSelector } from 'src/hooks/useSelector';

interface AnalyticParams {
    employerId: string;
    employerManagerId: string;
    settings: string;
}

const sendButtonClickEvent = (type: 'open' | 'close', analyticParams: AnalyticParams) =>
    Analytics.sendHHEventButtonClick(`resume_search_settings_${type}`, analyticParams);

const sendElementShownEvent = (element: HTMLElement, analyticParams: AnalyticParams) =>
    Analytics.sendHHEventElementShown(element, { name: 'resume_search_settings', ...analyticParams });

const useAnalytics = (settings: Record<string, boolean>) => {
    const employerId = useSelector((state) => state.employerId);
    const employerManagerId = useSelector((state) => state.employerManager?.id) || '';

    return useMemo(() => {
        const analyticParams = {
            employerId,
            employerManagerId,
            settings: JSON.stringify(settings),
        };
        return {
            sendButtonClickAnalytics: (type: 'open' | 'close') => sendButtonClickEvent(type, analyticParams),
            sendElementShownAnalytics: (element: HTMLElement) => sendElementShownEvent(element, analyticParams),
        };
    }, [employerId, employerManagerId, settings]);
};

export const useResumeSearchSettingsAnalytics = () => {
    const activeSettings = useSelector((state) => state.resumeSearchResult.settings.activeSettings);
    const availableSettings = useSelector((state) => state.resumeSearchResult.settings.availableSettings);
    const filtersOnTheRight = useSelector((state) => state.resumeSearchResult.settings.filtersOnTheRight);

    const settings = useMemo(() => {
        const result = availableSettings.reduce(
            (acc, setting) => ({ ...acc, [setting]: activeSettings.includes(setting) }),
            {} as Record<SettingType | SettingName, boolean>
        );
        if (filtersOnTheRight) {
            result[SettingName.ShowFiltersOnTheRight] = filtersOnTheRight.value;
        }
        return result;
    }, [activeSettings, availableSettings, filtersOnTheRight]);

    return useAnalytics(settings);
};

export const useVacancyResponsesSettingsAnalytics = () => {
    const viewFields = useResumeSettingsViewFields();

    const settings = useMemo(() => {
        return viewFields.reduce(
            (acc, field) => ({ ...acc, [field.name.toLowerCase()]: field.active }),
            {} as Record<string, boolean>
        );
    }, [viewFields]);

    return useAnalytics(settings);
};
